import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import logoIncrustation from "@images/logo_incrustation.png"
import { CoachOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { word } from "@modules/core/utils/i18n"
import { Check, CircleIcon, CirclePileIcon, PlusIcon } from "lucide-react"
import { type ReactNode } from "react"

type CoachOfferStep1ViewProps = {
  onSelectType: (type: CoachOfferType) => void
} & React.ComponentProps<"div">

export function CoachOfferStep1View({ onSelectType, className, ...props }: CoachOfferStep1ViewProps) {
  return (
    <div className={cn("flex flex-col gap-16 px-5 pt-12 pb-8", className)} {...props}>
      <Text fontStyle="TL">{word("coach.offers.form.type.selection.title")}</Text>

      <div className="flex flex-col gap-2">
        <Text fontStyle="TS" className="text-textOnSurface-default">
          {word("coach.offers.form.accompanied.description")}
        </Text>
        <div className="flex flex-row items-center gap-4">
          {getTypeCards().map((card) => (
            <TypeCard key={card.type} {...card} onClick={() => onSelectType(card.type)} />
          ))}
        </div>
      </div>
    </div>
  )
}

type TypeCardInfo = {
  type: CoachOfferType
  icon: ReactNode
  advantages: string[]
}

type TypeCardProps = TypeCardInfo & { onClick: () => void } & React.ComponentProps<"div">

function TypeCard({ type, icon, advantages, className, onClick, ...props }: TypeCardProps) {
  return (
    <div
      className={cn(
        "relative flex h-full flex-col gap-4 overflow-hidden rounded-2xl bg-surface-background-secondary p-6",
        className,
      )}
      {...props}
    >
      <img
        src={logoIncrustation}
        alt=""
        aria-hidden
        className="pointer-events-none absolute right-0 bottom-0 w-3/4 opacity-10 invert select-none"
      />
      <div className="flex flex-row items-center gap-2">
        {icon}
        <Text fontStyle="TM" className="font-bold text-textOnSurface-default">
          {getTypeLabel(type)}
        </Text>
      </div>

      <div className="flex flex-col gap-3">
        <Text fontStyle="LS" className="text-textOnSurface-subtle">
          {word("coach.offers.form.advantages.subtitle")}
        </Text>

        {advantages.map((advantage, index) => (
          <div key={index} className="flex w-full items-start gap-2">
            <Check size={20} className="mt-0.5 shrink-0 text-textSuccess" />
            <Text fontStyle="LM" className="text-textOnSurface-default">
              {advantage}
            </Text>
          </div>
        ))}
      </div>

      <Button
        variant="primary"
        onClick={onClick}
        className="mt-auto flex w-full flex-row items-center justify-center gap-2"
      >
        <PlusIcon size={16} />
        {getTypeCta(type)}
      </Button>
    </div>
  )
}

function getTypeCards(): TypeCardInfo[] {
  return [
    {
      type: CoachOfferType.Individual,
      icon: <CircleIcon size={32} />,
      advantages: [
        word("coach.offers.form.type.individual.advantage1"),
        word("coach.offers.form.type.individual.advantage2"),
        word("coach.offers.form.type.individual.advantage3"),
      ],
    },
    {
      type: CoachOfferType.Collective,
      icon: <CirclePileIcon size={32} />,
      advantages: [
        word("coach.offers.form.type.collective.advantage1"),
        word("coach.offers.form.type.collective.advantage2"),
        word("coach.offers.form.type.collective.advantage3"),
      ],
    },
  ]
}

function getTypeLabel(type: CoachOfferType): string {
  switch (type) {
    case CoachOfferType.Individual:
      return word("coach.offers.type.individualPrograms")
    case CoachOfferType.Collective:
      return word("coach.offers.type.collectivePrograms")
  }
}

function getTypeCta(type: CoachOfferType): string {
  switch (type) {
    case CoachOfferType.Individual:
      return word("global.create")
    case CoachOfferType.Collective:
      return word("global.create")
  }
}
